import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Music, Eye, EyeOff, Mail, Lock, User, Check, Archive, AlertCircle } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Checkbox } from '@/components/ui/checkbox';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Alert, AlertDescription } from '@/components/ui/alert';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { useToast } from '@/hooks/use-toast';
import { useStore } from '@/lib/store';
import musicFriendsImage from '@/assets/music-friends.jpg';

const MusicLeagueTransfer = () => {
  const [showPassword, setShowPassword] = useState(false);
  const [formData, setFormData] = useState({
    username: '',
    email: '',
    password: '',
    confirmPassword: '',
    musicleagueUsername: '',
  });
  const [acceptTerms, setAcceptTerms] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [isFeedbackDialogOpen, setIsFeedbackDialogOpen] = useState(false);
  const navigate = useNavigate();
  const { toast } = useToast();
  const { registerUser, login } = useStore();

  const getPasswordStrength = (password: string) => {
    let strength = 0;
    if (password.length >= 8) strength++;
    if (/[A-Z]/.test(password)) strength++;
    if (/[0-9]/.test(password)) strength++;
    if (/[^A-Za-z0-9]/.test(password)) strength++;
    return strength;
  };

  const passwordStrength = getPasswordStrength(formData.password);
  const strengthLabels = ['Weak', 'Fair', 'Good', 'Strong'];
  const strengthColors = ['bg-red-500', 'bg-orange-500', 'bg-yellow-500', 'bg-green-500'];

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    e.stopPropagation();
    
    // Trim and normalize all inputs
    const trimmedUsername = formData.username.trim();
    const trimmedEmail = formData.email.trim().toLowerCase();
    const trimmedPassword = formData.password.trim();
    const trimmedConfirmPassword = formData.confirmPassword.trim();
    const trimmedMusicLeagueUsername = formData.musicleagueUsername.trim();

    // Validate MusicLeague username
    if (!trimmedMusicLeagueUsername) {
      toast({
        title: "MusicLeague username required",
        description: "Please enter your MusicLeague username to link your account.",
        variant: "destructive",
      });
      return;
    }

    // Validate username
    if (!trimmedUsername) {
      toast({
        title: "Username required",
        description: "Please enter a username.",
        variant: "destructive",
      });
      return;
    }

    if (trimmedUsername.length < 3) {
      toast({
        title: "Username too short",
        description: "Username must be at least 3 characters long.",
        variant: "destructive",
      });
      return;
    }

    // Validate email
    if (!trimmedEmail) {
      toast({
        title: "Email required",
        description: "Please enter your email address.",
        variant: "destructive",
      });
      return;
    }

    // Basic email validation
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(trimmedEmail)) {
      toast({
        title: "Invalid email",
        description: "Please enter a valid email address.",
        variant: "destructive",
      });
      return;
    }

    // Validate password
    if (!trimmedPassword) {
      toast({
        title: "Password required",
        description: "Please enter a password.",
        variant: "destructive",
      });
      return;
    }

    if (trimmedPassword.length < 8) {
      toast({
        title: "Password too short",
        description: "Password must be at least 8 characters long.",
        variant: "destructive",
      });
      return;
    }

    // Validate password match
    if (trimmedPassword !== trimmedConfirmPassword) {
      toast({
        title: "Password mismatch",
        description: "Please make sure your passwords match.",
        variant: "destructive",
      });
      return;
    }

    // Validate terms
    if (!acceptTerms) {
      toast({
        title: "Terms required",
        description: "Please accept the terms and conditions to continue.",
        variant: "destructive",
      });
      return;
    }

    setIsLoading(true);

    try {
      console.log('Attempting to transfer MusicLeague account:', { 
        username: trimmedUsername, 
        email: trimmedEmail, 
        musicleagueUsername: trimmedMusicLeagueUsername 
      });
      
      const result = await registerUser({
        username: trimmedUsername,
        email: trimmedEmail,
        password: trimmedPassword,
        musicleagueUsername: trimmedMusicLeagueUsername,
        isTransferPage: true, // Flag to indicate this is the transfer page
      });

      console.log('Transfer successful:', result);
      
      if (result.isImportedUser) {
        toast({
          title: "Account Linked!",
          description: `Welcome back, ${result.user.username}! Your MusicLeague account "${trimmedMusicLeagueUsername}" has been linked. Your archival data is now available.`,
        });
        navigate('/playlists');
      } else if (result.alreadyLinked) {
        // Account was already linked, user was logged in
        toast({
          title: "Account Already Linked",
          description: `Your account "${trimmedMusicLeagueUsername}" is already linked. You've been signed in.`,
        });
        navigate('/playlists');
      } else {
        toast({
          title: "Welcome to BeatBattle!",
          description: `Your account has been created successfully, ${result.user.username}!`,
        });
        navigate('/playlists');
      }
    } catch (error) {
      console.error('Transfer error:', error);
      const message =
        error instanceof Error ? error.message : 'We were unable to link your account. Please try again.';
      toast({
        title: "Transfer failed",
        description: message,
        variant: "destructive",
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleInputChange = (field: string, value: string) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  return (
    <div className="min-h-screen grid lg:grid-cols-2">
      {/* Left Side - Form */}
      <div className="flex items-center justify-center p-8 bg-background">
        <div className="w-full max-w-md space-y-8">
          {/* Logo */}
          <div className="text-center">
            <Link to="/" className="inline-flex items-center space-x-2 mb-8">
              <div className="w-10 h-10 rounded-lg bg-music-gradient flex items-center justify-center">
                <Music className="h-6 w-6 text-white" />
              </div>
              <span className="text-2xl font-bold text-foreground">BeatBattle</span>
            </Link>
          </div>

          <Card className="card-shadow border-2 border-primary/30 bg-gradient-to-br from-purple-100/40 to-blue-100/40 dark:from-purple-950/20 dark:to-blue-950/20 shadow-md">
            <CardHeader className="text-center bg-gradient-to-r from-primary/15 to-blue-500/15 dark:from-primary/20 dark:to-blue-400/20 rounded-t-lg border-b border-primary/30">
              <CardTitle className="text-2xl font-bold text-primary flex items-center justify-center gap-2">
                <Archive className="h-5 w-5" />
                Link Your MusicLeague Account
              </CardTitle>
              <p className="text-muted-foreground mt-2">
                Transfer your account to preserve your music competition history
              </p>
            </CardHeader>
            <CardContent>
              <Alert className="mb-6 border-blue-500 bg-blue-50 dark:bg-blue-900/20">
                <Archive className="h-4 w-4 text-blue-600 dark:text-blue-400" />
                <AlertDescription className="text-blue-800 dark:text-blue-200">
                  <strong>Why link your account?</strong> Linking your MusicLeague account allows you to access all your archival data including past submissions, votes, and league history from musicleague.com.
                </AlertDescription>
              </Alert>

              <form onSubmit={handleSubmit} className="space-y-6">
                <div className="space-y-2">
                  <Label htmlFor="musicleagueUsername">
                    MusicLeague Username <span className="text-red-500">*</span>
                  </Label>
                  <div className="relative">
                    <User className="absolute left-3 top-3 h-4 w-4 text-muted-foreground" />
                    <Input
                      id="musicleagueUsername"
                      type="text"
                      inputMode="text"
                      autoComplete="username"
                      placeholder="Your MusicLeague username"
                      className="pl-10"
                      value={formData.musicleagueUsername}
                      onChange={(e) => handleInputChange('musicleagueUsername', e.target.value)}
                      onBlur={(e) => handleInputChange('musicleagueUsername', e.target.value.trim())}
                      required
                    />
                  </div>
                  <p className="text-xs text-muted-foreground">
                    Enter the exact username you used on musicleague.com
                  </p>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="username">New Username</Label>
                  <div className="relative">
                    <User className="absolute left-3 top-3 h-4 w-4 text-muted-foreground" />
                    <Input
                      id="username"
                      type="text"
                      inputMode="text"
                      autoComplete="username"
                      placeholder="Choose a username"
                      className="pl-10"
                      value={formData.username}
                      onChange={(e) => handleInputChange('username', e.target.value)}
                      onBlur={(e) => handleInputChange('username', e.target.value.trim())}
                      required
                    />
                  </div>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="email">Email address</Label>
                  <div className="relative">
                    <Mail className="absolute left-3 top-3 h-4 w-4 text-muted-foreground" />
                    <Input
                      id="email"
                      type="email"
                      inputMode="email"
                      autoComplete="email"
                      placeholder="Enter your email"
                      className="pl-10"
                      value={formData.email}
                      onChange={(e) => handleInputChange('email', e.target.value)}
                      onBlur={(e) => handleInputChange('email', e.target.value.trim().toLowerCase())}
                      required
                    />
                  </div>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="password">Password</Label>
                  <div className="relative">
                    <Lock className="absolute left-3 top-3 h-4 w-4 text-muted-foreground" />
                    <Input
                      id="password"
                      type={showPassword ? 'text' : 'password'}
                      autoComplete="new-password"
                      placeholder="Create a password"
                      className="pl-10 pr-10"
                      value={formData.password}
                      onChange={(e) => handleInputChange('password', e.target.value)}
                      required
                    />
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      className="absolute right-0 top-0 h-full px-3 hover:bg-transparent"
                      onClick={() => setShowPassword(!showPassword)}
                    >
                      {showPassword ? (
                        <EyeOff className="h-4 w-4 text-muted-foreground" />
                      ) : (
                        <Eye className="h-4 w-4 text-muted-foreground" />
                      )}
                    </Button>
                  </div>

                  {/* Password Strength Indicator */}
                  {formData.password && (
                    <div className="space-y-2">
                      <div className="flex space-x-1">
                        {[1, 2, 3, 4].map((level) => (
                          <div
                            key={level}
                            className={`h-1 w-full rounded ${
                              passwordStrength >= level
                                ? strengthColors[passwordStrength - 1]
                                : 'bg-muted'
                            }`}
                          />
                        ))}
                      </div>
                      <p className="text-xs text-muted-foreground">
                        Password strength: {strengthLabels[passwordStrength - 1] || 'Too weak'}
                      </p>
                    </div>
                  )}
                </div>

                <div className="space-y-2">
                  <Label htmlFor="confirmPassword">Confirm Password</Label>
                  <div className="relative">
                    <Lock className="absolute left-3 top-3 h-4 w-4 text-muted-foreground" />
                    <Input
                      id="confirmPassword"
                      type="password"
                      autoComplete="new-password"
                      placeholder="Confirm your password"
                      className="pl-10"
                      value={formData.confirmPassword}
                      onChange={(e) => handleInputChange('confirmPassword', e.target.value)}
                      required
                    />
                    {formData.confirmPassword && formData.password === formData.confirmPassword && (
                      <Check className="absolute right-3 top-3 h-4 w-4 text-green-500" />
                    )}
                  </div>
                </div>

                <div className="flex items-center space-x-2">
                  <Checkbox
                    id="terms"
                    checked={acceptTerms}
                    onCheckedChange={(checked) => setAcceptTerms(checked === true)}
                  />
                  <Label htmlFor="terms" className="text-sm">
                    I agree to the{' '}
                    <Link to="/terms" className="text-primary hover:underline">
                      Terms of Service
                    </Link>{' '}
                    and{' '}
                    <Link to="/privacy" className="text-primary hover:underline">
                      Privacy Policy
                    </Link>
                  </Label>
                </div>

                <Button
                  type="submit"
                  className="w-full btn-music"
                  disabled={isLoading || !acceptTerms}
                >
                  {isLoading ? 'Linking account...' : 'Link Account'}
                </Button>

                <Alert className="border-yellow-500 bg-yellow-50 dark:bg-yellow-900/20">
                  <AlertCircle className="h-4 w-4 text-yellow-600 dark:text-yellow-400" />
                  <AlertDescription className="text-yellow-800 dark:text-yellow-200 text-sm">
                    <strong>Already linked?</strong> If your account is already linked, you'll be signed in automatically. If you think your account has been linked in error, please{' '}
                    <button
                      type="button"
                      onClick={() => setIsFeedbackDialogOpen(true)}
                      className="text-primary hover:underline font-medium"
                    >
                      contact us via the feedback form
                    </button>
                    .
                  </AlertDescription>
                </Alert>

                <p className="text-center text-sm text-muted-foreground">
                  Don't have a MusicLeague account?{' '}
                  <Link to="/signup" className="text-primary hover:underline font-medium">
                    Create a new account
                  </Link>
                </p>
              </form>
            </CardContent>
          </Card>
        </div>
      </div>

      {/* Right Side - Image */}
      <div className="hidden lg:block relative">
        <img
          src={musicFriendsImage}
          alt="Music competition with friends"
          className="absolute inset-0 h-full w-full object-cover"
        />
        <div className="absolute inset-0 bg-music-gradient/20" />
        <div className="absolute inset-0 flex items-center justify-center p-12">
          <div className="text-center text-white">
            <h2 className="text-4xl font-bold mb-4">
              Preserve Your Music History
            </h2>
            <p className="text-xl opacity-90">
              Link your MusicLeague account to access all your past competitions, submissions, and votes.
            </p>
          </div>
        </div>
      </div>

      {/* Feedback Dialog */}
      <Dialog open={isFeedbackDialogOpen} onOpenChange={setIsFeedbackDialogOpen}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-hidden p-0">
          <DialogHeader className="px-6 pt-6 pb-4">
            <DialogTitle>Feedback</DialogTitle>
          </DialogHeader>
          <div className="px-6 pb-6">
            <iframe
              src="https://app.smartsuite.com/form/s1ck0hy9/MdvdD3RbrM?header=false"
              width="100%"
              height="600px"
              frameBorder="0"
              className="rounded-md"
            />
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default MusicLeagueTransfer;

